import React, { useEffect, useMemo, useState, useCallback, useDeferredValue, useRef } from 'react'
import { Row, Col, Spinner, Container, Alert } from 'react-bootstrap'
import { FiCheck, FiPlus } from 'react-icons/fi'
import useFetch from '../../../_fetch'
import { ClickDataTable } from './click-table'
import { formatNumberLocale, formatNumber, formatLocalDateForInput, getLocalDateStart, getLocalDateEnd } from "../../../_utils"
import { useAuth } from '../../../auth/auth-context'
import { ClicksPerPath, HourlyClickRow, IPublisher, Infos, ITransaction, ImpressionsPerPath, IPartnership } from './types'
import LinkKpiOverview from './link-kpi-overview'
import DateRangeSelector from '../home/dashboard-content/date-range-selector'
import { loadPref, resolveLabel } from '../home/dashboard-content/date-range-preference'
import DomainPathFilter from './domain-path-filter'
import { DomainPathFilterSelection, isFilterActive, loadFilterPref } from './domain-path-filter-preference'
import HourlyClicksChart, {
    defaultLinkPerformanceGroupBy,
    defaultLinkPerformanceMetrics,
    getStoredLinkPerformanceGroupBy,
    getStoredLinkPerformanceMetrics,
    getLinkPerformanceRequestMetrics,
    LinkPerformanceGroupBy,
    LinkPerformanceMetric,
    linkPerformanceGroupByStorageKey,
    linkPerformanceMetricStorageKey,
} from './hourly-clicks-chart'
import PagePerformanceChart, { pageLineColors, PagePerformanceMetric, PagePerformanceRow } from './page-performance-chart'

type DashboardPageRow = {
    domain: string
    pathId: number
    path: string
    tracked: number
    untracked: number
    total: number
    totalTransactions: number
    totalCommission: number
    impressions: number
}

type DashboardLinkRow = {
    linkId: number
    url: string
    NPClicks: number
    Clicks: number
    totalTransactions: number
    totalCommission: number
}

type DashboardCampaignRow = {
    campaignId: number | string
    name: string
    network: string
    publisherName: string
    clicks: number
    totalTransactions: number
    totalCommission: number
}

type DailyClickRow = {
    date: string
    linkId: number
    pathId: number
    clicks: number
    automatedClicks?: number
    untrackedClicks?: number
    url?: string
}

type HourlyLinkPathClickRow = DailyClickRow & {
    hour: string
    automatedClicks?: number
    untrackedClicks?: number
}

type DashboardDiagnostics = {
    droppedRefs?: number
    linkRowsTotal?: number
    linkRowsReturned?: number
    linkRowsLimit?: number
    linkRowsTruncated?: boolean
}

type LinkScriptChartMode = 'current' | 'compare' | 'pages'
type PagePerformanceSelectionPreset = 'top-5' | 'top-10' | 'top-20' | 'top-30' | 'custom' | 'none'

type DateRangeValue = {
    fromDate: string
    toDate: string
}
/*
RPM
Page views
Total clicks
*/
const getDrillLevel = (prefix: string | null): number => {
    if (!prefix) return 1
    const firstSlash = prefix.indexOf('/')
    if (firstSlash === -1) return 1
    const pathPart = prefix.slice(firstSlash)
    return pathPart.split('/').filter(Boolean).length + 1
}

const buildBreadcrumbs = (prefix: string | null): { label: string; prefix: string | null }[] => {
    const crumbs: { label: string; prefix: string | null }[] = [{ label: 'Overview', prefix: null }]
    if (!prefix) return crumbs
    const firstSlash = prefix.indexOf('/')
    if (firstSlash === -1) return crumbs
    const domain = prefix.slice(0, firstSlash)
    const pathPart = prefix.slice(firstSlash)
    const segments = pathPart.split('/').filter(Boolean)
    let pathSoFar = ''
    for (const seg of segments) {
        pathSoFar += '/' + seg
        crumbs.push({ label: '/' + seg + '/', prefix: domain + pathSoFar + '/' })
    }
    return crumbs
}

const cleanLabel = (value: unknown, fallback = 'Unknown'): string => {
    if (typeof value !== 'string') return fallback
    const normalized = value.trim()
    if (!normalized) return fallback
    const lowered = normalized.toLowerCase()
    if (lowered === 'undefined' || lowered === 'null' || lowered === 'nan') return fallback
    return normalized
}

const domainColumnMinWidth = 'clamp(180px, 28vw, 260px)'
const DAY_MS = 24 * 60 * 60 * 1000
const DEFAULT_PAGE_PERFORMANCE_SELECTION_COUNT = 10

function parseLocalDate(value: string | Date | null | undefined, endOfDay = false): Date | null {
    if (!value) return null
    if (value instanceof Date) {
        const parsedDate = new Date(value)
        if (endOfDay) parsedDate.setHours(23, 59, 59, 999)
        else parsedDate.setHours(0, 0, 0, 0)
        return Number.isFinite(parsedDate.getTime()) ? parsedDate : null
    }

    if (value.includes('T')) {
        const parsedDate = new Date(value)
        if (endOfDay) parsedDate.setHours(23, 59, 59, 999)
        else parsedDate.setHours(0, 0, 0, 0)
        return Number.isFinite(parsedDate.getTime()) ? parsedDate : null
    }

    const parsed = new Date(`${value}${endOfDay ? 'T23:59:59.999' : 'T00:00:00.000'}`)
    return Number.isFinite(parsed.getTime()) ? parsed : null
}

function getDateRangeDayCount(from: string | Date, to: string | Date) {
    const start = parseLocalDate(from)
    const end = parseLocalDate(to)
    if (!start || !end) return 1

    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)

    const firstTime = Math.min(start.getTime(), end.getTime())
    const lastTime = Math.max(start.getTime(), end.getTime())

    return Math.max(1, Math.floor((lastTime - firstTime) / DAY_MS) + 1)
}

function getPreviousDateRange(from: string, to: string): DateRangeValue {
    const start = parseLocalDate(from)
    const end = parseLocalDate(to)
    if (!start || !end) return { fromDate: from, toDate: to }

    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)

    const first = start <= end ? start : end
    const last = start <= end ? end : start
    const dayCount = getDateRangeDayCount(first, last)
    const previousEnd = new Date(first)
    previousEnd.setDate(previousEnd.getDate() - 1)
    const previousStart = new Date(previousEnd)
    previousStart.setDate(previousStart.getDate() - dayCount + 1)

    return {
        fromDate: formatLocalDateForInput(previousStart),
        toDate: formatLocalDateForInput(previousEnd),
    }
}

function getAllowedLinkPerformanceGroupBy(from: string, to: string): LinkPerformanceGroupBy[] {
    const dayCount = getDateRangeDayCount(from, to)
    if (dayCount >= 10) return ['day']
    return ['hour', 'day']
}

function getAutomaticLinkPerformanceGroupBy(
    currentGroupBy: LinkPerformanceGroupBy,
    from: string,
    to: string
): LinkPerformanceGroupBy {
    const dayCount = getDateRangeDayCount(from, to)
    if (dayCount <= 2) return 'hour'
    if (dayCount >= 10) return 'day'
    return resolveLinkPerformanceGroupBy(currentGroupBy, from, to)
}

function resolveLinkPerformanceGroupBy(
    requestedGroupBy: LinkPerformanceGroupBy | null | undefined,
    from: string,
    to: string
): LinkPerformanceGroupBy {
    const allowed = getAllowedLinkPerformanceGroupBy(from, to)
    const fallback = allowed[0] || defaultLinkPerformanceGroupBy
    return requestedGroupBy && allowed.includes(requestedGroupBy) ? requestedGroupBy : fallback
}

function isPartnershipActiveOnDate(partnership: IPartnership, date: string) {
    const clickDate = parseLocalDate(date)
    const partnershipStart = parseLocalDate(partnership.startDate)
    const partnershipEnd = parseLocalDate(partnership.expiryDate, true)

    if (!clickDate) return false
    if (partnershipStart && partnershipStart > clickDate) return false
    if (partnershipEnd && partnershipEnd < clickDate) return false

    return true
}

function normalizeDomain(value: string | null | undefined) {
    if (!value) return ''
    const trimmed = value.trim().toLowerCase()
    if (!trimmed) return ''

    try {
        return new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`).hostname.replace(/^www\./, '')
    } catch {
        return trimmed
            .replace(/^https?:\/\//, '')
            .split('/')[0]
            .split(':')[0]
            .replace(/^www\./, '')
    }
}

function getPageKey(domain: string, path: string) {
    return `${domain}\u0000${path}`
}

function addNumber(map: Map<string, number>, key: string, value: number) {
    map.set(key, (map.get(key) || 0) + value)
}

function addNumberById(map: Map<number, number>, key: number, value: number) {
    map.set(key, (map.get(key) || 0) + value)
}

function safeDivide(numerator: number, denominator: number) {
    const parsedNumerator = Number(numerator) || 0
    const parsedDenominator = Number(denominator) || 0
    if (parsedDenominator <= 0) return 0
    return parsedNumerator / parsedDenominator
}

function getTotalLinkClicks(row: { NPClicks?: number; Clicks?: number }) {
    return (Number(row.NPClicks) || 0) + (Number(row.Clicks) || 0)
}

function getTopPagePerformancePathIds(rows: DashboardPageRow[], count: number) {
    return [...rows]
        .sort((first, second) => {
            const pageViewsDiff = (Number(second.impressions) || 0) - (Number(first.impressions) || 0)
            if (pageViewsDiff !== 0) return pageViewsDiff
            return (Number(second.total) || 0) - (Number(first.total) || 0)
        })
        .slice(0, count)
        .map((row) => Number(row.pathId))
        .filter(Boolean)
}

function getPagePerformanceSelectionContextKey(fromDate: string, toDate: string, selection: DomainPathFilterSelection) {
    return JSON.stringify({ fromDate, toDate, selection })
}

function isMobileViewport() {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
}

function getPagePerformanceColor(index: number) {
    if (index < pageLineColors.length) return pageLineColors[index]

    // The golden angle keeps adjacent generated colors visually separated while
    // saturation/lightness variations let the palette continue beyond one hue cycle.
    const generatedIndex = index - pageLineColors.length
    const hue = (generatedIndex * 137.508) % 360
    const saturation = 62 + (Math.floor(generatedIndex / 12) % 3) * 8
    const lightness = 42 + (Math.floor(generatedIndex / 36) % 3) * 7
    return `hsl(${hue.toFixed(3)}, ${saturation}%, ${lightness}%)`
}

function getStablePagePerformanceColors(currentColors: Map<number, string>, selectedPathIds: number[]) {
    const nextColors = new Map<number, string>()
    const usedColors = new Set<string>()

    for (const pathId of selectedPathIds) {
        const existingColor = currentColors.get(pathId)
        if (!existingColor || usedColors.has(existingColor)) continue

        nextColors.set(pathId, existingColor)
        usedColors.add(existingColor)
    }

    for (const pathId of selectedPathIds) {
        if (nextColors.has(pathId)) continue

        let colorIndex = 0
        let nextColor = getPagePerformanceColor(colorIndex)
        while (usedColors.has(nextColor)) {
            colorIndex += 1
            nextColor = getPagePerformanceColor(colorIndex)
        }

        nextColors.set(pathId, nextColor)
        usedColors.add(nextColor)
    }

    return nextColors
}

const LinkScriptDashboard: React.FC<{publishers: IPublisher[]}> = ({ publishers }) => {
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const [infos, setInfos] = useState<Pick<Infos, 'domains' | 'paths'>>({ domains: [], paths: [] })

    const [filterSelection, setFilterSelection] = useState<DomainPathFilterSelection>(() => loadFilterPref())
    const [useMobileTableWidths, setUseMobileTableWidths] = useState(() => isMobileViewport())

    const [serverPageRows, setServerPageRows] = useState<DashboardPageRow[]>([])
    const [serverLinkRows, setServerLinkRows] = useState<DashboardLinkRow[]>([])
    const [serverCampaignRows, setServerCampaignRows] = useState<DashboardCampaignRow[]>([])
    const [serverDailyClicks, setServerDailyClicks] = useState<DailyClickRow[]>([])
    const [serverHourlyClicksByLinkPath, setServerHourlyClicksByLinkPath] = useState<HourlyLinkPathClickRow[]>([])
    const [serverKpiClicksPerPath, setServerKpiClicksPerPath] = useState<ClicksPerPath[]>([])
    const [serverKpiBadClicksPerPath, setServerKpiBadClicksPerPath] = useState<ClicksPerPath[]>([])
    const [serverKpiImpressionsPerPath, setServerKpiImpressionsPerPath] = useState<ImpressionsPerPath[]>([])
    const [serverKpiTransactions, setServerKpiTransactions] = useState<ITransaction[]>([])
    const [serverHourlyClicks, setServerHourlyClicks] = useState<HourlyClickRow[]>([])
    const [serverCompareHourlyClicks, setServerCompareHourlyClicks] = useState<HourlyClickRow[]>([])
    const [serverCompareHourlyClicksByLinkPath, setServerCompareHourlyClicksByLinkPath] = useState<HourlyLinkPathClickRow[]>([])
    const [serverPagePerformanceRows, setServerPagePerformanceRows] = useState<PagePerformanceRow[]>([])
    const deferredServerPagePerformanceRows = useDeferredValue(serverPagePerformanceRows)
    const [serverDiagnostics, setServerDiagnostics] = useState<DashboardDiagnostics>({})
    const [partnerships, setPartnerships] = useState<IPartnership[]>([])
    const [chartMode, setChartMode] = useState<LinkScriptChartMode>('current')
    const chartModeRef = useRef<LinkScriptChartMode>('current')
    const [compareDateRange, setCompareDateRange] = useState<DateRangeValue | null>(null)
    const [selectedPagePerformancePathIds, setSelectedPagePerformancePathIds] = useState<number[]>([])
    const deferredSelectedPagePerformancePathIds = useDeferredValue(selectedPagePerformancePathIds)
    const selectedPagePerformancePathIdsRef = useRef<number[]>([])
    const [pagePerformanceColorByPathId, setPagePerformanceColorByPathId] = useState<Map<number, string>>(new Map())
    const deferredPagePerformanceColorByPathId = useDeferredValue(pagePerformanceColorByPathId)
    const pagePerformanceColorByPathIdRef = useRef<Map<number, string>>(new Map())
    const pagePerformanceSelectionContextRef = useRef<string>('')
    const pagePerformanceRowsContextRef = useRef<string>('')
    const serverPagePerformanceRowsRef = useRef<PagePerformanceRow[]>([])
    const [pagePerformanceMetric, setPagePerformanceMetric] = useState<PagePerformanceMetric>('pageViews')
    const [addingPagePerformancePathId, setAddingPagePerformancePathId] = useState<number | null>(null)
    const isAddingPagePerformancePathRef = useRef(false)
    const [selectedChartMetrics, setSelectedChartMetrics] = useState<LinkPerformanceMetric[]>(() => (
        getStoredLinkPerformanceMetrics(user?.username) || defaultLinkPerformanceMetrics
    ))
    const selectedChartMetricsRef = useRef<LinkPerformanceMetric[]>(selectedChartMetrics)

    const [subPathDrillPrefix, setSubPathDrillPrefix] = useState<string | null>(null)
    const deferredDrillPrefix = useDeferredValue(subPathDrillPrefix)
    const isSubPathTransitioning = deferredDrillPrefix !== subPathDrillPrefix
    const subPathDrillLevel = useMemo(() => getDrillLevel(subPathDrillPrefix), [subPathDrillPrefix])
    

    const [loading, setLoading] = useState(true)

    const [fromDate, setFromDate] = useState(() => {
        const saved = resolveLabel(loadPref('link-script'))
        if (saved) return formatLocalDateForInput(saved.startDate)
        return formatLocalDateForInput(new Date())
    })

    const [toDate, setToDate] = useState(() => {
        const saved = resolveLabel(loadPref('link-script'))
        if (saved) return formatLocalDateForInput(saved.endDate)
        return formatLocalDateForInput(new Date())
    })
    const [selectedChartGroupBy, setSelectedChartGroupBy] = useState<LinkPerformanceGroupBy>(() => (
        getAutomaticLinkPerformanceGroupBy(getStoredLinkPerformanceGroupBy(user?.username), fromDate, toDate)
    ))
    const selectedChartGroupByRef = useRef<LinkPerformanceGroupBy>(selectedChartGroupBy)

    useEffect(() => {
        if (typeof window === 'undefined') return

        const mediaQuery = window.matchMedia('(max-width: 767px)')
        const updateViewport = () => setUseMobileTableWidths(mediaQuery.matches)

        updateViewport()
        mediaQuery.addEventListener?.('change', updateViewport)
        return () => mediaQuery.removeEventListener?.('change', updateViewport)
    }, [])

    useEffect(() => {
        const storedMetrics = getStoredLinkPerformanceMetrics(user?.username)
        const nextMetrics = storedMetrics || defaultLinkPerformanceMetrics
        const nextGroupBy = getAutomaticLinkPerformanceGroupBy(getStoredLinkPerformanceGroupBy(user?.username), fromDate, toDate)
        selectedChartMetricsRef.current = nextMetrics
        selectedChartGroupByRef.current = nextGroupBy
        setSelectedChartMetrics(nextMetrics)
        setSelectedChartGroupBy(nextGroupBy)
    }, [fromDate, toDate, user?.username])

    useEffect(() => {
        selectedChartMetricsRef.current = selectedChartMetrics
    }, [selectedChartMetrics])

    useEffect(() => {
        selectedChartGroupByRef.current = selectedChartGroupBy
    }, [selectedChartGroupBy])

    useEffect(() => {
        chartModeRef.current = chartMode
    }, [chartMode])

    useEffect(() => {
        selectedPagePerformancePathIdsRef.current = selectedPagePerformancePathIds
    }, [selectedPagePerformancePathIds])

    useEffect(() => {
        pagePerformanceColorByPathIdRef.current = pagePerformanceColorByPathId
    }, [pagePerformanceColorByPathId])

    useEffect(() => {
        serverPagePerformanceRowsRef.current = serverPagePerformanceRows
    }, [serverPagePerformanceRows])

    const updateSelectedPagePerformancePathIds = useCallback((nextPathIds: number[], resetColors = false) => {
        const nextColors = getStablePagePerformanceColors(
            resetColors ? new Map() : pagePerformanceColorByPathIdRef.current,
            nextPathIds
        )

        setSelectedPagePerformancePathIds(nextPathIds)
        selectedPagePerformancePathIdsRef.current = nextPathIds
        setPagePerformanceColorByPathId(nextColors)
        pagePerformanceColorByPathIdRef.current = nextColors
    }, [])

    const fetchClicks = useCallback(async (
        from?: string,
        to?: string,
        selectionOverride?: DomainPathFilterSelection,
        chartMetricsOverride?: LinkPerformanceMetric[],
        chartGroupByOverride?: LinkPerformanceGroupBy,
        chartModeOverride?: LinkScriptChartMode,
        pagePerformancePathIdsOverride?: number[],
        pagePerformanceAutoSelectOverride?: boolean,
        pagePerformancePreserveSelectionOverride = false
    ) => {
        setLoading(true)
        try {
            const fromValue = from || fromDate
            const toValue = to || toDate
            const mode = chartModeOverride || chartModeRef.current
            const previousRange = getPreviousDateRange(fromValue, toValue)
            const finalFrom = getLocalDateStart(fromValue)
            const finalTo = getLocalDateEnd(toValue)
            const compareFrom = getLocalDateStart(previousRange.fromDate)
            const compareTo = getLocalDateEnd(previousRange.toDate)
            const selection = selectionOverride || filterSelection
            const pagePerformanceSelectionContextKey = getPagePerformanceSelectionContextKey(fromValue, toValue, selection)
            const pagePerformanceSelectionIsCurrent = pagePerformanceSelectionContextRef.current === pagePerformanceSelectionContextKey
            const requestedGroupBy = chartGroupByOverride || selectedChartGroupByRef.current || getStoredLinkPerformanceGroupBy(user?.username)
            const effectiveGroupBy = resolveLinkPerformanceGroupBy(requestedGroupBy, fromValue, toValue)
            const shouldAutoSelectPagePerformance = mode === 'pages' && (
                pagePerformanceAutoSelectOverride === true ||
                (!pagePerformancePathIdsOverride && (
                    selectedPagePerformancePathIdsRef.current.length === 0 ||
                    !pagePerformanceSelectionIsCurrent
                ))
            )
            const pagePerformancePathIds = shouldAutoSelectPagePerformance
                ? []
                : pagePerformancePathIdsOverride || selectedPagePerformancePathIdsRef.current
            let clickPartnerships: IPartnership[] = []

            selectedChartGroupByRef.current = effectiveGroupBy
            setSelectedChartGroupBy(effectiveGroupBy)

            if (user?.kind === 'publisher' && user?.partnerships?.enabled) {
                const resPartnerships = await doFetch('/byelink/partnerships?type=click')
                if (resPartnerships.status < 400) {
                    const data = await resPartnerships.json()
                    clickPartnerships = Array.isArray(data) ? data : []
                    setPartnerships(clickPartnerships)
                } else {
                    setPartnerships([])
                }
            } else {
                setPartnerships([])
            }

            const resClicks = await doFetch('/byelink/link-script-dashboard', {
                fromDate: finalFrom.toISOString(),
                toDate: finalTo.toISOString(),
                timezoneOffsetMinutes: new Date().getTimezoneOffset(),
                filterSelection: selection,
                includeDailyClicks: clickPartnerships.length > 0,
                hourlyMetrics: getLinkPerformanceRequestMetrics(
                    chartMetricsOverride || getStoredLinkPerformanceMetrics(user?.username) || selectedChartMetricsRef.current
                ),
                chartGroupBy: effectiveGroupBy,
                compareFromDate: mode === 'compare' ? compareFrom.toISOString() : undefined,
                compareToDate: mode === 'compare' ? compareTo.toISOString() : undefined,
                pagePerformancePathIds: mode === 'pages' ? pagePerformancePathIds : [],
                pagePerformanceAutoSelect: shouldAutoSelectPagePerformance,
            })

            if (resClicks.status >= 400) {
                throw await resClicks.text()
            }

            const clicksJson = await resClicks.json()

            const nextInfos = clicksJson?.infos || { domains: [], paths: [] }
            setInfos({ domains: nextInfos.domains || [], paths: nextInfos.paths || [] })

            setServerPageRows(clicksJson?.pageRows || [])
            setServerLinkRows(clicksJson?.linkRows || [])
            setServerCampaignRows(clicksJson?.campaignRows || [])
            setServerDailyClicks(clicksJson?.dailyClicks || clicksJson?.dailyAutomatedClicks || [])
            setServerHourlyClicksByLinkPath(clicksJson?.hourlyClicksByLinkPath || [])

            setServerKpiClicksPerPath(clicksJson?.kpis?.clicksPerPath || [])
            setServerKpiBadClicksPerPath(clicksJson?.kpis?.badClicksPerPath || [])
            setServerKpiImpressionsPerPath(clicksJson?.kpis?.impressionsPerPath || [])
            setServerKpiTransactions(clicksJson?.kpis?.transactions || [])
            setServerHourlyClicks(clicksJson?.kpis?.hourlyClicks || [])
            setServerCompareHourlyClicks(clicksJson?.compare?.hourlyClicks || [])
            setServerCompareHourlyClicksByLinkPath(clicksJson?.compare?.hourlyClicksByLinkPath || [])
            if (mode === 'pages') {
                const responseSelectedPathIds = Array.isArray(clicksJson?.pagePerformance?.selectedPathIds)
                    ? clicksJson.pagePerformance.selectedPathIds.map(Number).filter(Boolean)
                    : []
                const nextSelectedPathIds = pagePerformancePreserveSelectionOverride
                    ? selectedPagePerformancePathIdsRef.current
                    : pagePerformancePathIdsOverride || responseSelectedPathIds
                const incomingPagePerformanceRows = clicksJson?.pagePerformance?.rows || []
                const incomingPathIds = new Set(responseSelectedPathIds)
                const previousPagePerformanceRows = pagePerformanceRowsContextRef.current === pagePerformanceSelectionContextKey
                    ? serverPagePerformanceRowsRef.current
                    : []
                const nextPagePerformanceRows = [
                    ...previousPagePerformanceRows.filter((row) => !incomingPathIds.has(Number(row.pathId))),
                    ...incomingPagePerformanceRows,
                ]

                setServerPagePerformanceRows(nextPagePerformanceRows)
                serverPagePerformanceRowsRef.current = nextPagePerformanceRows
                updateSelectedPagePerformancePathIds(nextSelectedPathIds, shouldAutoSelectPagePerformance)
                pagePerformanceSelectionContextRef.current = pagePerformanceSelectionContextKey
                pagePerformanceRowsContextRef.current = pagePerformanceSelectionContextKey
            } else {
                const nextPagePerformanceRows = clicksJson?.pagePerformance?.rows || []
                setServerPagePerformanceRows(nextPagePerformanceRows)
                serverPagePerformanceRowsRef.current = nextPagePerformanceRows
                pagePerformanceRowsContextRef.current = ''
            }
            setCompareDateRange(mode === 'compare' ? previousRange : null)
            setServerDiagnostics(clicksJson?.diagnostics || {})

        } catch (err) {
            console.error('Error fetching clicks:', err)
        } finally {
            setLoading(false)
        }
    }, [doFetch, fromDate, toDate, filterSelection, updateSelectedPagePerformancePathIds, user?.kind, user?.partnerships?.enabled, user?.username])

    const updateChartSettings = useCallback(async (metrics: LinkPerformanceMetric[], groupBy: LinkPerformanceGroupBy) => {
        const effectiveGroupBy = resolveLinkPerformanceGroupBy(groupBy, fromDate, toDate)
        try {
            localStorage.setItem(linkPerformanceMetricStorageKey(user?.username), JSON.stringify(metrics))
            localStorage.setItem(linkPerformanceGroupByStorageKey(user?.username), effectiveGroupBy)
        } catch {
            // Non-critical: the chart still updates and refetches if browser storage is unavailable.
        }
        selectedChartMetricsRef.current = metrics
        selectedChartGroupByRef.current = effectiveGroupBy
        setSelectedChartMetrics(metrics)
        setSelectedChartGroupBy(effectiveGroupBy)
        await fetchClicks(
            undefined,
            undefined,
            undefined,
            metrics,
            effectiveGroupBy,
            undefined,
            chartModeRef.current === 'pages' ? selectedPagePerformancePathIdsRef.current : undefined
        )
    }, [fetchClicks, fromDate, toDate, user?.username])

    useEffect(() => {
        const fetchData = async () => {
            try {
                await fetchClicks()
            } catch (err) {
                console.error('Error fetching initial data:', err)
                setLoading(false)
            }
        }
        fetchData()
    }, [fetchClicks])

    // Returns true if a domain+path combo passes the active filter (prefix-match)
    const isRowAllowed = useCallback((domainStr: string, pathStr: string): boolean => {
        if (!isFilterActive(filterSelection)) return true
        const matchedDomain = infos.domains.find(d => d.domain === domainStr)
        if (!matchedDomain) return false
        const selectedDomainIds = Object.keys(filterSelection.domains).map(Number)
        if (!selectedDomainIds.includes(matchedDomain.id)) return false
        const selectedPrefixes = filterSelection.domains[matchedDomain.id]
        // empty = all paths for this domain
        if (!selectedPrefixes || selectedPrefixes.length === 0) return true
        // prefix-match: pathStr must start with one of the selected top-level prefixes
        return selectedPrefixes.some(prefix => pathStr.startsWith(prefix))
    }, [filterSelection, infos.domains])

    const cpcAdjustments = useMemo(() => {
        const clickPartnershipsByDomain = new Map<string, IPartnership[]>()

        for (const partnership of partnerships) {
            if (partnership.type !== 'click') continue
            const value = Number(partnership.dashboardValue ?? partnership.value) || 0
            if (value <= 0) continue

            for (const domain of partnership.domains || []) {
                const normalizedDomain = normalizeDomain(domain)
                if (!normalizedDomain) continue
                const current = clickPartnershipsByDomain.get(normalizedDomain) || []
                current.push(partnership)
                clickPartnershipsByDomain.set(normalizedDomain, current)
            }
        }

        const pageCommissionByKey = new Map<string, number>()
        const linkCommissionByUrl = new Map<string, number>()
        const partnershipCommissionById = new Map<string, number>()
        const qualifyingClicksByPageKey = new Map<string, number>()
        const qualifyingClicksByLinkUrl = new Map<string, number>()
        const qualifyingClicksByPartnershipId = new Map<string, number>()
        const qualifyingHourlyClicksByPathId = new Map<number, Map<string, number>>()
        const partnershipCommissionByHour = new Map<string, number>()
        const partnershipCommissionByPathHour = new Map<string, number>()
        const comparePartnershipCommissionByHour = new Map<string, number>()
        const comparePartnershipCommissionByPathHour = new Map<string, number>()
        const promotedClicksByPathId = new Map<number, number>()
        const pageByPathId = new Map<number, DashboardPageRow>()
        const linkByLinkId = new Map<number, DashboardLinkRow>()
        let total = 0

        for (const row of serverPageRows) {
            pageByPathId.set(Number(row.pathId), row)
        }

        for (const row of serverLinkRows) {
            linkByLinkId.set(Number(row.linkId), row)
        }

        for (const row of serverDailyClicks) {
            const link = linkByLinkId.get(Number(row.linkId))
            const page = pageByPathId.get(Number(row.pathId))
            const targetUrl = link?.url || row.url || ''
            if (!targetUrl || !page) continue

            const targetDomain = normalizeDomain(targetUrl)
            const matchingPartnerships = clickPartnershipsByDomain.get(targetDomain)
            if (!matchingPartnerships?.length) continue

            for (const partnership of matchingPartnerships) {
                if (!isPartnershipActiveOnDate(partnership, row.date)) continue

                const clicks = Number(row.untrackedClicks ?? row.clicks) || 0
                const commission = clicks * (Number(partnership.dashboardValue ?? partnership.value) || 0)
                if (commission <= 0) continue

                const pageKey = getPageKey(page.domain, page.path)
                addNumber(pageCommissionByKey, pageKey, commission)
                addNumber(linkCommissionByUrl, targetUrl, commission)
                addNumber(partnershipCommissionById, partnership.id, commission)
                addNumber(qualifyingClicksByPageKey, pageKey, clicks)
                addNumber(qualifyingClicksByLinkUrl, targetUrl, clicks)
                addNumber(qualifyingClicksByPartnershipId, partnership.id, clicks)
                total += commission
            }
        }

        for (const row of serverHourlyClicksByLinkPath) {
            const link = linkByLinkId.get(Number(row.linkId))
            const page = pageByPathId.get(Number(row.pathId))
            const targetUrl = link?.url || row.url || ''
            if (!targetUrl || !page || !row.hour) continue

            const targetDomain = normalizeDomain(targetUrl)
            const matchingPartnerships = clickPartnershipsByDomain.get(targetDomain)
            if (!matchingPartnerships?.length) continue

            const activePartnerships = matchingPartnerships.filter((partnership) => isPartnershipActiveOnDate(partnership, row.date))
            if (activePartnerships.length <= 0) continue

            const pathId = Number(row.pathId)
            const untrackedClicks = Number(row.untrackedClicks) || 0
            const clicks = untrackedClicks * activePartnerships.length
            if (!pathId || clicks <= 0) continue

            for (const partnership of activePartnerships) {
                const value = Number(partnership.dashboardValue ?? partnership.value) || 0
                if (value > 0) {
                    addNumber(partnershipCommissionByHour, row.hour, untrackedClicks * value)
                    addNumber(partnershipCommissionByPathHour, `${pathId}\u0000${row.hour}`, untrackedClicks * value)
                }
            }

            const byHour = qualifyingHourlyClicksByPathId.get(pathId) || new Map<string, number>()
            byHour.set(row.hour, (byHour.get(row.hour) || 0) + clicks)
            qualifyingHourlyClicksByPathId.set(pathId, byHour)
        }

        const collectCompareHourlyAdjustments = () => {
            const promotedClicksByHour = new Map<string, number>()
            const promotedClicksByPathHour = new Map<string, number>()

            for (const row of serverCompareHourlyClicksByLinkPath) {
                const pathId = Number(row.pathId)
                const page = pageByPathId.get(pathId)
                const targetUrl = row.url || linkByLinkId.get(Number(row.linkId))?.url || ''
                if (!targetUrl || !page || !row.hour) continue

                const targetDomain = normalizeDomain(targetUrl)
                const matchingPartnerships = clickPartnershipsByDomain.get(targetDomain)
                if (!matchingPartnerships?.length) continue

                const activePartnerships = matchingPartnerships.filter((partnership) => isPartnershipActiveOnDate(partnership, row.date))
                if (activePartnerships.length <= 0) continue

                const untrackedClicks = Number(row.untrackedClicks) || 0
                if (!pathId || untrackedClicks <= 0) continue

                const promotedClicks = untrackedClicks * activePartnerships.length
                addNumber(promotedClicksByHour, row.hour, promotedClicks)
                addNumber(promotedClicksByPathHour, `${pathId}\u0000${row.hour}`, promotedClicks)

                for (const partnership of activePartnerships) {
                    const value = Number(partnership.dashboardValue ?? partnership.value) || 0
                    if (value <= 0) continue
                    const commission = untrackedClicks * value
                    addNumber(comparePartnershipCommissionByHour, row.hour, commission)
                    addNumber(comparePartnershipCommissionByPathHour, `${pathId}\u0000${row.hour}`, commission)
                }
            }

            return { promotedClicksByHour, promotedClicksByPathHour }
        }

        const promotedClicksByPageKey = new Map<string, number>()
        const promotedClicksByLinkUrl = new Map<string, number>()
        const promotedClicksByHour = new Map<string, number>()
        const promotedClicksByPathHour = new Map<string, number>()

        for (const row of serverPageRows) {
            const pageKey = getPageKey(row.domain, row.path)
            const qualifyingClicks = qualifyingClicksByPageKey.get(pageKey) || 0
            const untracked = Number(row.untracked) || 0
            const promoted = Math.min(untracked, qualifyingClicks)
            if (promoted > 0) {
                promotedClicksByPageKey.set(pageKey, promoted)
                addNumberById(promotedClicksByPathId, Number(row.pathId), promoted)

                let remaining = promoted
                const hourlyClicks = Array.from(qualifyingHourlyClicksByPathId.get(Number(row.pathId))?.entries() || [])
                    .sort(([hourA], [hourB]) => hourA.localeCompare(hourB))

                for (const [hour, clicks] of hourlyClicks) {
                    if (remaining <= 0) break
                    const promotedForHour = Math.min(remaining, clicks)
                    if (promotedForHour > 0) {
                        addNumber(promotedClicksByHour, hour, promotedForHour)
                        addNumber(promotedClicksByPathHour, `${Number(row.pathId)}\u0000${hour}`, promotedForHour)
                        remaining -= promotedForHour
                    }
                }
            }
        }

        const compareHourlyAdjustments = collectCompareHourlyAdjustments()

        for (const row of serverLinkRows) {
            const qualifyingClicks = qualifyingClicksByLinkUrl.get(row.url) || 0
            const untracked = Number(row.Clicks) || 0
            const promoted = Math.min(untracked, qualifyingClicks)
            if (promoted > 0) promotedClicksByLinkUrl.set(row.url, promoted)
        }

        const clickCampaignRows = partnerships
            .filter((partnership) => partnership.type === 'click')
            .map((partnership, index): DashboardCampaignRow | null => {
                const clicks = qualifyingClicksByPartnershipId.get(partnership.id) || 0
                const totalCommission = partnershipCommissionById.get(partnership.id) || 0
                if (clicks <= 0 && totalCommission <= 0) return null

                return {
                    campaignId: `click-${partnership.id || index}`,
                    name: cleanLabel(partnership.name, 'Click partnership'),
                    network: 'Nordpar',
                    publisherName: 'Click agreement',
                    clicks,
                    totalTransactions: 0,
                    totalCommission,
                }
            })
            .filter((row): row is DashboardCampaignRow => row !== null)

        return {
            pageCommissionByKey,
            linkCommissionByUrl,
            promotedClicksByPageKey,
            promotedClicksByLinkUrl,
            promotedClicksByPathId,
            promotedClicksByHour,
            promotedClicksByPathHour,
            partnershipCommissionByHour,
            partnershipCommissionByPathHour,
            comparePromotedClicksByHour: compareHourlyAdjustments.promotedClicksByHour,
            comparePromotedClicksByPathHour: compareHourlyAdjustments.promotedClicksByPathHour,
            comparePartnershipCommissionByHour,
            comparePartnershipCommissionByPathHour,
            clickCampaignRows,
            total,
        }
    }, [partnerships, serverCompareHourlyClicksByLinkPath, serverDailyClicks, serverHourlyClicksByLinkPath, serverLinkRows, serverPageRows])

    const pageOverviewRows = useMemo(() => {
        return serverPageRows.map((row) => {
            const pageKey = getPageKey(row.domain, row.path)
            const promotedClicks = cpcAdjustments.promotedClicksByPageKey.get(pageKey) || 0

            return {
                ...row,
                tracked: row.tracked + promotedClicks,
                untracked: Math.max(0, row.untracked - promotedClicks),
                totalCommission: row.totalCommission + (cpcAdjustments.pageCommissionByKey.get(pageKey) || 0),
            }
        })
    }, [cpcAdjustments, serverPageRows])

    const filteredPathOverview = useMemo(() => {
        return pageOverviewRows.filter(row => isRowAllowed(row.domain, row.path))
    }, [pageOverviewRows, isRowAllowed])

    const adjustedKpiClicksPerPath = useMemo(() => {
        const rowsByPathId = new Map<number, ClicksPerPath>()

        for (const row of serverKpiClicksPerPath) {
            rowsByPathId.set(Number(row.path_id), { ...row })
        }

        for (const [pathId, promotedClicks] of Array.from(cpcAdjustments.promotedClicksByPathId.entries())) {
            const existing = rowsByPathId.get(pathId) || { path_id: pathId, clicks: 0 }
            existing.clicks = (Number(existing.clicks) || 0) + promotedClicks
            rowsByPathId.set(pathId, existing)
        }

        return Array.from(rowsByPathId.values())
    }, [cpcAdjustments.promotedClicksByPathId, serverKpiClicksPerPath])

    const adjustedKpiBadClicksPerPath = useMemo(() => {
        return serverKpiBadClicksPerPath.map((row) => {
            const promotedClicks = cpcAdjustments.promotedClicksByPathId.get(Number(row.path_id)) || 0
            return {
                ...row,
                clicks: Math.max(0, (Number(row.clicks) || 0) - promotedClicks),
            }
        })
    }, [cpcAdjustments.promotedClicksByPathId, serverKpiBadClicksPerPath])

    const adjustedHourlyClicks = useMemo(() => {
        return serverHourlyClicks.map((row) => {
            const promotedClicks = cpcAdjustments.promotedClicksByHour.get(row.hour) || 0
            const partnershipCommission = cpcAdjustments.partnershipCommissionByHour.get(row.hour) || 0
            return {
                ...row,
                automatedClicks: Math.min(
                    Number(row.totalClicks) || 0,
                    (Number(row.automatedClicks) || 0) + promotedClicks
                ),
                commission: (Number(row.commission) || 0) + partnershipCommission,
            }
        })
    }, [cpcAdjustments.partnershipCommissionByHour, cpcAdjustments.promotedClicksByHour, serverHourlyClicks])

    const domainOverview = useMemo(() => {
        const domainMap = new Map<string, {
            domain: string
            tracked: number
            untracked: number
            total: number
            totalTransactions: number
            totalCommission: number
            impressions: number
        }>()

        for (const row of filteredPathOverview) {
            const current = domainMap.get(row.domain) || {
                domain: row.domain,
                tracked: 0,
                untracked: 0,
                total: 0,
                totalTransactions: 0,
                totalCommission: 0,
                impressions: 0,
            }

            current.tracked += row.tracked || 0
            current.untracked += row.untracked || 0
            current.total += row.total || 0
            current.totalTransactions += row.totalTransactions || 0
            current.totalCommission += row.totalCommission || 0
            current.impressions += row.impressions || 0

            domainMap.set(row.domain, current)
        }

        return Array.from(domainMap.values())
    }, [filteredPathOverview])

    const filteredDomainOverview = domainOverview.filter(d => d.total > 0 || d.tracked > 0 || d.impressions > 0 || d.totalTransactions > 0 || d.totalCommission > 0)

    const subPathOverview = useMemo(() => {
        const level = getDrillLevel(deferredDrillPrefix)
        if (level > 4) return []

        const groupedRows = new Map<string, {
            page: string
            tracked: number
            untracked: number
            total: number
            totalTransactions: number
            totalCommission: number
            impressions: number
        }>()

        for (const row of filteredPathOverview) {
            const fullKey = `${row.domain}${row.path}`
            if (deferredDrillPrefix && !fullKey.startsWith(deferredDrillPrefix)) continue

            const pathSegments = row.path.split('/').filter(Boolean)
            if (pathSegments.length < level) continue

            const groupedPath = '/' + pathSegments.slice(0, level).join('/') + '/'
            const page = `${row.domain}${groupedPath}`

            const existing = groupedRows.get(page) || {
                page, tracked: 0, untracked: 0, total: 0,
                totalTransactions: 0, totalCommission: 0, impressions: 0
            }
            existing.tracked += row.tracked
            existing.untracked += row.untracked
            existing.total += row.total
            existing.totalTransactions += row.totalTransactions
            existing.totalCommission += row.totalCommission
            existing.impressions += row.impressions
            groupedRows.set(page, existing)
        }

        return Array.from(groupedRows.values())
    }, [filteredPathOverview, deferredDrillPrefix])

    useEffect(() => {
        if (!subPathDrillPrefix) return
        const hasMatch = filteredPathOverview.some(row => `${row.domain}${row.path}`.startsWith(subPathDrillPrefix))
        if (!hasMatch) {
            setSubPathDrillPrefix(null)
        }
    }, [subPathDrillPrefix, filteredPathOverview])
    const linkOverviewRows = useMemo(() => {
        return serverLinkRows.map((row) => {
            const promotedClicks = cpcAdjustments.promotedClicksByLinkUrl.get(row.url) || 0

            return {
                ...row,
                NPClicks: row.NPClicks + promotedClicks,
                Clicks: Math.max(0, row.Clicks - promotedClicks),
                totalCommission: row.totalCommission + (cpcAdjustments.linkCommissionByUrl.get(row.url) || 0),
            }
        })
    }, [cpcAdjustments, serverLinkRows])

    const campaignOverviewRows = useMemo(() => {
        const publisherByInternalId = new Map<number, IPublisher>()
        for (const publisher of publishers) {
            publisherByInternalId.set(publisher.internalId, publisher)
        }

        const existingRows = serverCampaignRows.map((row) => {
            const publisher = publisherByInternalId.get(Number(row.campaignId))
            return {
                ...row,
                name: cleanLabel(publisher?.name, cleanLabel(row.name)),
                network: cleanLabel(publisher?.network, cleanLabel(row.network)),
                publisherName: cleanLabel(publisher?.publisherName, cleanLabel(row.publisherName)),
            }
        })

        return [...existingRows, ...cpcAdjustments.clickCampaignRows]
    }, [cpcAdjustments.clickCampaignRows, publishers, serverCampaignRows])

    const chartGroupByOptions = useMemo(() => getAllowedLinkPerformanceGroupBy(fromDate, toDate), [fromDate, toDate])
    const effectiveSelectedChartGroupBy = useMemo(
        () => resolveLinkPerformanceGroupBy(selectedChartGroupBy, fromDate, toDate),
        [fromDate, selectedChartGroupBy, toDate]
    )
    const updatePagePerformanceGroupBy = useCallback(
        (groupBy: LinkPerformanceGroupBy) => updateChartSettings(selectedChartMetricsRef.current, groupBy),
        [updateChartSettings]
    )
    const pagePerformanceSelectionPreset = useMemo<PagePerformanceSelectionPreset>(() => {
        if (deferredSelectedPagePerformancePathIds.length === 0) return 'none'

        const selected = new Set(deferredSelectedPagePerformancePathIds)
        for (const count of [5, 10, 20, 30]) {
            const topPathIds = getTopPagePerformancePathIds(filteredPathOverview, count)
            if (topPathIds.length === selected.size && topPathIds.every((pathId) => selected.has(pathId))) {
                return `top-${count}` as PagePerformanceSelectionPreset
            }
        }

        return 'custom'
    }, [deferredSelectedPagePerformancePathIds, filteredPathOverview])
    const selectedPagePerformancePages = useMemo(() => {
        const selected = new Set(deferredSelectedPagePerformancePathIds)
        return filteredPathOverview
            .filter((row) => selected.has(Number(row.pathId)))
            .map((row) => ({
                pathId: Number(row.pathId),
                domain: row.domain,
                path: row.path,
                impressions: row.impressions,
                totalClicks: row.total,
            }))
    }, [deferredSelectedPagePerformancePathIds, filteredPathOverview])

    const changeChartMode = useCallback(async (nextMode: LinkScriptChartMode) => {
        let nextPagePathIds = selectedPagePerformancePathIdsRef.current
        const shouldAutoSelectPages = nextMode === 'pages'

        if (shouldAutoSelectPages) {
            nextPagePathIds = getTopPagePerformancePathIds(filteredPathOverview, DEFAULT_PAGE_PERFORMANCE_SELECTION_COUNT)
            updateSelectedPagePerformancePathIds(nextPagePathIds, true)
            pagePerformanceSelectionContextRef.current = ''
        }

        setChartMode(nextMode)
        chartModeRef.current = nextMode

        if (nextMode === 'current') {
            setCompareDateRange(null)
            setServerCompareHourlyClicks([])
            return
        }

        await fetchClicks(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            nextMode,
            nextPagePathIds,
            false
        )
    }, [fetchClicks, filteredPathOverview, updateSelectedPagePerformancePathIds])

    const togglePagePerformancePath = useCallback(async (pathId: number) => {
        const current = selectedPagePerformancePathIdsRef.current
        const alreadySelected = current.includes(pathId)
        if (!alreadySelected && isAddingPagePerformancePathRef.current) return

        const nextPathIds = alreadySelected
            ? current.filter((id) => id !== pathId)
            : [...current, pathId]

        updateSelectedPagePerformancePathIds(nextPathIds)
        setChartMode('pages')
        chartModeRef.current = 'pages'

        const currentContextKey = getPagePerformanceSelectionContextKey(fromDate, toDate, filterSelection)
        const hasCachedPageRows = pagePerformanceRowsContextRef.current === currentContextKey &&
            serverPagePerformanceRowsRef.current.some((row) => Number(row.pathId) === pathId)

        if (alreadySelected || hasCachedPageRows) {
            return
        }

        isAddingPagePerformancePathRef.current = true
        setAddingPagePerformancePathId(pathId)
        try {
            await fetchClicks(undefined, undefined, undefined, undefined, undefined, 'pages', [pathId], false, true)
        } finally {
            isAddingPagePerformancePathRef.current = false
            setAddingPagePerformancePathId(null)
        }
    }, [fetchClicks, filterSelection, fromDate, toDate, updateSelectedPagePerformancePathIds])

    const selectTopPagePerformancePaths = useCallback(async (count: number) => {
        if (loading || isAddingPagePerformancePathRef.current) return

        const nextPathIds = getTopPagePerformancePathIds(filteredPathOverview, count)
        updateSelectedPagePerformancePathIds(nextPathIds, true)
        setChartMode('pages')
        chartModeRef.current = 'pages'

        await fetchClicks(undefined, undefined, undefined, undefined, undefined, 'pages', nextPathIds, false)
    }, [fetchClicks, filteredPathOverview, loading, updateSelectedPagePerformancePathIds])

    const clearPagePerformancePaths = useCallback(() => {
        if (loading || isAddingPagePerformancePathRef.current) return
        updateSelectedPagePerformancePathIds([], true)
    }, [loading, updateSelectedPagePerformancePathIds])

    const chartIsRefreshing = loading && serverPageRows.length > 0
    const deferredChartIsRefreshing = useDeferredValue(chartIsRefreshing)

    return (
        <Container fluid>
            <div className="link-script-header mb-4">
                <div>
                    <h3 className="mb-0">Link automation</h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="link-script-date-selector">
                        <DateRangeSelector
                            pageKey="link-script"
                            defaultLabel="Today"
                            onSubmit={async (startDate, endDate) => {
                                const nextFromDate = formatLocalDateForInput(startDate)
                                const nextToDate = formatLocalDateForInput(endDate)
                                const nextGroupBy = getAutomaticLinkPerformanceGroupBy(selectedChartGroupByRef.current, nextFromDate, nextToDate)

                                setFromDate(nextFromDate)
                                setToDate(nextToDate)
                                selectedChartGroupByRef.current = nextGroupBy
                                setSelectedChartGroupBy(nextGroupBy)
                                try {
                                    localStorage.setItem(linkPerformanceGroupByStorageKey(user?.username), nextGroupBy)
                                } catch {
                                    // Non-critical: range submit should still refetch with the enforced grouping.
                                }
                                await fetchClicks(
                                    nextFromDate,
                                    nextToDate,
                                    undefined,
                                    undefined,
                                    nextGroupBy,
                                    undefined,
                                    undefined,
                                    chartModeRef.current === 'pages'
                                )
                            }}
                        />
                    </div>
                    <DomainPathFilter
                        domains={infos.domains}
                        paths={infos.paths}
                        value={filterSelection}
                        onApply={async (selection) => {
                            setFilterSelection(selection)
                            setSubPathDrillPrefix(null)
                            await fetchClicks(
                                undefined,
                                undefined,
                                selection,
                                undefined,
                                undefined,
                                undefined,
                                undefined,
                                chartModeRef.current === 'pages'
                            )
                        }}
                    />
                </div>
            </div>

            <hr />

            {loading && serverPageRows.length === 0 ? (
                <Row>
                    <Col className="text-center">
                        <Spinner animation="border" />
                    </Col>
                </Row>
            ) : (
                <>
                        <LinkKpiOverview
                            clicksPerPath={adjustedKpiClicksPerPath}
                            badClicksPerPath={adjustedKpiBadClicksPerPath}
                            impressionsPerPath={serverKpiImpressionsPerPath}
                            transactions={serverKpiTransactions}
                            commissionAdjustment={cpcAdjustments.total}
                            currency={user?.settings.currency || ''}
                        />
                        <div className="link-script-chart-shell">
                            <div className="link-script-chart-modebar">
                                {([
                                    ['current', 'Overall'],
                                    ['compare', 'Compare'],
                                    ['pages', 'Pages'],
                                ] as Array<[LinkScriptChartMode, string]>).map(([mode, label]) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        className={chartMode === mode ? 'active' : ''}
                                        onClick={() => changeChartMode(mode)}
                                        disabled={loading && serverPageRows.length === 0}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div className="position-relative">
                                {chartIsRefreshing && chartMode !== 'pages' && (
                                    <div className="link-script-chart-loading">
                                        <span className="spinner-border spinner-border-sm" />
                                    </div>
                                )}
                                {chartMode === 'pages' ? (
                                    <PagePerformanceChart
                                        rows={deferredServerPagePerformanceRows}
                                        selectedPages={selectedPagePerformancePages}
                                        metric={pagePerformanceMetric}
                                        currency={user?.settings.currency || ''}
                                        fromDate={fromDate}
                                        toDate={toDate}
                                        groupBy={effectiveSelectedChartGroupBy}
                                        groupByOptions={chartGroupByOptions}
                                        colorByPathId={deferredPagePerformanceColorByPathId}
                                        loading={deferredChartIsRefreshing}
                                        actionsDisabled={loading || addingPagePerformancePathId !== null}
                                        selectionPreset={pagePerformanceSelectionPreset}
                                        onMetricChange={setPagePerformanceMetric}
                                        onGroupByChange={updatePagePerformanceGroupBy}
                                        onSelectTop={selectTopPagePerformancePaths}
                                        onClear={clearPagePerformancePaths}
                                    />
                                ) : (
                                    <HourlyClicksChart
                                        rows={adjustedHourlyClicks}
                                        compareRows={chartMode === 'compare' ? serverCompareHourlyClicks : []}
                                        currency={user?.settings.currency || ''}
                                        fromDate={fromDate}
                                        toDate={toDate}
                                        compareFromDate={compareDateRange?.fromDate}
                                        compareToDate={compareDateRange?.toDate}
                                        title={chartMode === 'compare' ? 'Performance over time compare' : 'Performance over time'}
                                        selectedMetrics={selectedChartMetrics}
                                        selectedGroupBy={effectiveSelectedChartGroupBy}
                                        groupByOptions={chartGroupByOptions}
                                        onSelectedSettingsChange={updateChartSettings}
                                    />
                                )}
                            </div>
                        </div>
                    <Row>

                        <Col xs={12}>
                            <ClickDataTable
                                title="Page click report"
                                data={filteredPathOverview}
                                exportDataVersion={`${chartMode}:${user?.settings.currency || ''}`}
                                    columns={[
                                        ...(chartMode === 'pages' ? [{
                                            name: 'Graph',
                                            cell: (row: DashboardPageRow) => {
                                                const pathId = Number(row.pathId)
                                                const selected = selectedPagePerformancePathIds.includes(pathId)
                                                const disabled = !selected && (loading || addingPagePerformancePathId !== null)
                                                const performanceColor = pagePerformanceColorByPathId.get(pathId)
                                                const performanceStyle = selected && performanceColor
                                                    ? { '--link-script-performance-color': performanceColor } as React.CSSProperties
                                                    : undefined

                                                return (
                                                    <button
                                                        type="button"
                                                        className={`link-script-performance-toggle${selected ? ' is-selected' : ''}`}
                                                        disabled={disabled}
                                                        style={performanceStyle}
                                                        aria-pressed={selected}
                                                        title={disabled ? 'Wait for the page to finish loading' : selected ? 'Remove from graph' : 'Add to graph'}
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            togglePagePerformancePath(pathId)
                                                        }}
                                                    >
                                                        <span className="link-script-performance-toggle-icon">
                                                            {selected ? <FiCheck /> : <FiPlus />}
                                                        </span>
                                                        <span>{selected ? 'Shown' : 'Add'}</span>
                                                    </button>
                                                )
                                            },
                                            ignoreRowClick: true,
                                            button: true,
                                            width: '104px',
                                        }] : []),
                                        { 
                                            name: 'Page', 
                                            selector: row => row.domain + row.path, 
                                            sortable: true, 
                                            ...(useMobileTableWidths
                                                ? { width: '150px', minWidth: '120px', maxWidth: '170px' }
                                                : { width: '40%' }),
                                            wrap: true 
                                        },
                                        { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true, minWidth: '125px' },
                                        { name: 'CTR', selector: row => safeDivide(row.total, row.impressions), format: row => `${formatNumberLocale(safeDivide(row.total, row.impressions) * 100, 1)}%`, sortable: true, width: '80px' },
                                        { name: 'Automated Clicks', selector: row => row.tracked, format: row => formatNumberLocale(row.tracked, 0), sortable: true, id: 'NPClicks', minWidth: '195px' },
                                        { name: 'Total Clicks', selector: row => row.total, format: row => formatNumberLocale(row.total, 0), sortable: true, id: 'Total', minWidth: '135px' },
                                        { name: 'CR', selector: row => safeDivide(row.totalTransactions, row.total), format: row => `${formatNumberLocale(safeDivide(row.totalTransactions, row.total) * 100, 2)}%`, sortable: true, width: '80px' },
                                        { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, minWidth:'165px' },
                                        { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true, minWidth:'140px' },
                                        { name: 'EPC', selector: row => safeDivide(row.totalCommission, row.total), format: row => formatNumber(safeDivide(row.totalCommission, row.total), user?.settings.currency || ''), sortable: true, width: '100px' },
                                        { name: 'EPV', selector: row => safeDivide(row.totalCommission, row.impressions), format: row => formatNumber(safeDivide(row.totalCommission, row.impressions),user?.settings.currency || ''), sortable: true, width: '100px'},
                                    ]}
                                id='PageViews'
                            />
                        </Col>

                       <Col xs={12}>
                            {serverDiagnostics.linkRowsTruncated && (
                                <Alert variant="info" className="mb-3">
                                    Showing {formatNumberLocale(serverDiagnostics.linkRowsReturned || 0, 0)} of {formatNumberLocale(serverDiagnostics.linkRowsTotal || 0, 0)} links in the link report. Totals in the other reports still include all links.
                                </Alert>
                            )}
                            <ClickDataTable
                                title="Link click report"
                                data={linkOverviewRows}
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[25, 50, 100, 200]}
                                columns={[
                                    { name: 'Target URL', selector: row => row.url, sortable: true, wrap:true, width:'40%' },
                                    { name: 'Automated Clicks', selector: row => row.NPClicks, format: row => formatNumberLocale(row.NPClicks, 0), sortable: true, id: 'NPClicks', minWidth: '195px' },
                                    { 
                                        name: 'Total Clicks', 
                                        selector: row => getTotalLinkClicks(row), 
                                        format: row => formatNumberLocale(getTotalLinkClicks(row), 0), 
                                        sortable: true 
                                    },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, minWidth:'165px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => safeDivide(row.totalCommission, getTotalLinkClicks(row)), format: row => formatNumber(safeDivide(row.totalCommission, getTotalLinkClicks(row)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='NPClicks'
                            />
                        </Col>



                        <Col xs={12}>
                            <ClickDataTable
                                title="Campaign overview"
                                data={campaignOverviewRows}
                                columns={[
                                    { name: 'Name', selector: row => row.name, sortable: true },
                                    { name: 'Network', selector: row => row.network, sortable: true },
                                    { name: 'Publisher', selector: row => row.publisherName, sortable: true },
                                    { name: 'Automated Clicks', selector: row => row.clicks, format: row => formatNumberLocale(row.clicks, 0), sortable: true, id: 'Clicks', minWidth: '195px' },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, minWidth:'165px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => safeDivide(row.totalCommission, row.clicks), format: row => formatNumber(safeDivide(row.totalCommission, row.clicks), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='Clicks'
                            />
                        </Col>

                        <Col xs={12}>
                            {subPathDrillPrefix && (() => {
                                const crumbs = buildBreadcrumbs(subPathDrillPrefix)
                                return (
                                    <div className="d-flex align-items-center gap-1 small px-1 mb-1">
                                        {crumbs.map((crumb, i) => {
                                            const isLast = i === crumbs.length - 1
                                            return (
                                                <React.Fragment key={i}>
                                                    {i > 0 && <span className="text-muted">/</span>}
                                                    {isLast ? (
                                                        <span className="text-dark fw-semibold">{crumb.label}</span>
                                                    ) : (
                                                        <button
                                                            className="btn btn-link btn-sm p-0 text-muted text-decoration-none"
                                                            onClick={() => setSubPathDrillPrefix(crumb.prefix)}
                                                        >
                                                            {crumb.label}
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}
                                    </div>
                                )
                            })()}
                            <div style={{ opacity: isSubPathTransitioning ? 0.5 : 1, transition: 'opacity 0.15s' }}>
                            <ClickDataTable
                                title="Sub-path overview"
                                data={subPathOverview}
                                columns={[
                                    { name: 'Page', selector: row => row.page, sortable: true, width: '40%', wrap: true },
                                    { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true, minWidth: '125px' },
                                    { name: 'CTR', selector: row => safeDivide(row.total, row.impressions), format: row => `${formatNumberLocale(safeDivide(row.total, row.impressions) * 100, 1)}%`, sortable: true, width: '80px' },
                                    { name: 'Automated Clicks', selector: row => row.tracked, format: row => formatNumberLocale(row.tracked, 0), sortable: true, id: 'NPClicks', minWidth: '195px' },
                                    { name: 'Total Clicks', selector: row => row.total, format: row => formatNumberLocale(row.total, 0), sortable: true, id: 'Total', minWidth: '135px' },
                                    { name: 'CR', selector: row => safeDivide(row.totalTransactions, row.total), format: row => `${formatNumberLocale(safeDivide(row.totalTransactions, row.total) * 100, 2)}%`, sortable: true, width: '80px' },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, minWidth:'165px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true, minWidth:'140px' },
                                    { name: 'EPC', selector: row => safeDivide(row.totalCommission, row.total), format: row => formatNumber(safeDivide(row.totalCommission, row.total), user?.settings.currency || ''), sortable: true, width: '100px' },
                                    { name: 'EPV', selector: row => safeDivide(row.totalCommission, row.impressions), format: row => formatNumber(safeDivide(row.totalCommission, row.impressions),user?.settings.currency || ''), sortable: true, width: '100px'},
                                ]}
                                id='PageViews'
                                onRowClicked={subPathDrillLevel < 4
                                    ? (row) => setSubPathDrillPrefix(row.page)
                                    : undefined}
                                pointerOnHover={subPathDrillLevel < 4}
                            />
                            </div>
                        </Col>

                        <Col xs={12}>
                            <ClickDataTable
                                title="Domain overview"
                                data={filteredDomainOverview.map(d => ({
                                    domain: d.domain,
                                    NPClicks: d.tracked || 0,      // Mindst 0
                                    Clicks: d.untracked || 0,      // Mindst 0
                                    totalClicks: d.total || 0,
                                    totalTransactions: d.totalTransactions || 0,
                                    totalCommission: d.totalCommission || 0,
                                    impressions: d.impressions || 0

                                }))}
                                columns={[
                                    { name: 'Domain', selector: row => row.domain, sortable: true, minWidth: domainColumnMinWidth, grow: 2 },
                                    { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true, minWidth: '125px' },
                                    { name: 'CTR', selector: row => safeDivide(row.totalClicks, row.impressions), format: row => `${formatNumberLocale(safeDivide(row.totalClicks, row.impressions) * 100, 1)}%`, sortable: true, width: '80px' },
                                    { name: 'Automated Clicks', id: 'NPClicks', selector: row => row.NPClicks, format: row => formatNumberLocale(row.NPClicks, 0), sortable: true, minWidth: '195px' },
                                    { 
                                        name: 'Total Clicks', 
                                        selector: row => row.totalClicks, 
                                        format: row => formatNumberLocale(row.totalClicks, 0), 
                                        sortable: true, 
                                        id:'Total',
                                        minWidth: '135px'
                                    },
                                    { name: 'CR', selector: row => safeDivide(row.totalTransactions, row.totalClicks), format: row => `${formatNumberLocale(safeDivide(row.totalTransactions, row.totalClicks) * 100, 2)}%`, sortable: true, width: '80px' },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, minWidth:'165px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true, minWidth: '140px' },
                                    {name: 'EPC', selector: row => safeDivide(row.totalCommission, row.totalClicks), format: row => formatNumber(safeDivide(row.totalCommission, row.totalClicks), user?.settings.currency || ''), sortable: true, width: '100px'},
                                    {name: 'EPV', selector: row => safeDivide(row.totalCommission, row.impressions), format: row => formatNumber(safeDivide(row.totalCommission, row.impressions), user?.settings.currency || ''), sortable: true, width: '100px'}
                                ]}
                                id='PageViews'
                                height="200px"
                            />
                        </Col>

                    </Row>
                </>
            )}
        </Container>
    )
}

export default LinkScriptDashboard
